// DuplicateCardCleaner.js
/**
 * Utility for detecting and removing duplicate cards after they've been added
 */

const DuplicateCardCleaner = {
  /**
   * Identify and remove duplicate cards from the card collection
   * @param {Array} cards - The current array of cards
   * @param {Function} saveCards - Function to save the updated card array
   * @returns {Object} Results containing removed duplicates and the cleaned card array
   */
  cleanupDuplicates(cards, saveCards) {
    if (!Array.isArray(cards) || cards.length <= 1) {
      return { 
        removedDuplicates: [], 
        cleanedCards: cards || [], 
        hasDuplicates: false 
      };
    }
    
    const uniqueCardMap = new Map();
    const duplicates = [];
    
    // First pass: identify unique cards by creating a fingerprint
    cards.forEach(card => {
      const fingerprint = this.createCardFingerprint(card);
      
      if (!uniqueCardMap.has(fingerprint)) {
        // If this is the first card with this fingerprint, store it
        uniqueCardMap.set(fingerprint, card);
      } else {
        // If we already have a card with this fingerprint, it's a duplicate
        // Keep the older card (the one already in the map)
        const existingCard = uniqueCardMap.get(fingerprint);
        
        // Determine which card to keep (usually the older one)
        const cardToKeep = new Date(existingCard.createdAt) <= new Date(card.createdAt) 
          ? existingCard 
          : card;
          
        const cardToRemove = cardToKeep === existingCard ? card : existingCard;
        
        // Update the map with the card we want to keep
        uniqueCardMap.set(fingerprint, cardToKeep);
        
        // Add the other to duplicates
        duplicates.push({
          duplicate: cardToRemove,
          original: cardToKeep
        });
      }
    });
    
    // Convert map values back to array
    const cleanedCards = Array.from(uniqueCardMap.values());
    
    // Save the cleaned cards if we found duplicates
    if (duplicates.length > 0 && typeof saveCards === 'function') {
      saveCards(cleanedCards);
    }
    
    return {
      removedDuplicates: duplicates,
      cleanedCards: cleanedCards,
      hasDuplicates: duplicates.length > 0
    };
  },
  
  /**
   * Create a unique fingerprint for a card based on its content
   * @param {Object} card - The card to create a fingerprint for
   * @returns {String} A string fingerprint that identifies similar cards
   */
  createCardFingerprint(card) {
    if (!card) return '';
    
    // Normalize values to handle edge cases
    const title = (card.title || '').toLowerCase().trim();
    const url = this.normalizeUrl(card.url || '');
    const desc = (card.description || '').toLowerCase().trim();
    
    // Create fingerprint components
    const components = [];
    
    // Title is the most important identifier
    if (title) components.push(`t:${title}`);
    
    // URL is a strong secondary identifier
    if (url) components.push(`u:${url}`);
    
    // For cards without URL, use description as secondary identifier
    if (!url && desc) {
      // Use a truncated version of description to avoid minor differences
      components.push(`d:${desc.substring(0, 100)}`);
    }
    
    // Add normalized tags if they exist
    if (Array.isArray(card.tags) && card.tags.length > 0) {
      const normalizedTags = card.tags
        .map(tag => tag.toLowerCase().trim())
        .sort()
        .join(',');
      components.push(`tags:${normalizedTags}`);
    }
    
    return components.join('|');
  },
  
  /**
   * Normalize a URL for comparison
   * @param {String} url - The URL to normalize
   * @returns {String} A normalized URL string
   */
  normalizeUrl(url) {
    if (!url) return '';
    
    return url
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '')
      .trim();
  }
};

export default DuplicateCardCleaner;